import { useState } from "react";
import http from "@/lib/http";

/**
 * Shared multi-select + archive/delete helpers for lead overview pages.
 *
 * Two destructive flows are supported:
 *   * Archive  → soft-delete (default). Lead disappears from overviews
 *                but is fully recoverable from the Archief page.
 *   * Delete   → hard delete (permanent). Used only from the small
 *                destructive button in the bulk bar.
 */
export function useLeadSelection(refetch) {
  const [selected, setSelected] = useState(new Set());
  const [confirmIds, setConfirmIds] = useState(null);    // hard-delete confirm
  const [archiveIds, setArchiveIds] = useState(null);    // archive prompt
  const [archiveReason, setArchiveReason] = useState("");

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleAll = (visibleIds) => {
    const allVisible = visibleIds.every((id) => selected.has(id));
    setSelected(allVisible ? new Set() : new Set(visibleIds));
  };

  const clear = () => setSelected(new Set());

  // Hard-delete flow ------------------------------------------------------
  const requestDelete = (ids) => {
    const arr = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (arr.length) setConfirmIds(arr);
  };
  const cancelDelete = () => setConfirmIds(null);
  const doDelete = async () => {
    const ids = confirmIds || [];
    if (!ids.length) { setConfirmIds(null); return; }
    try {
      if (ids.length === 1) {
        await http.delete(`/api/leads/${ids[0]}?hard=true`);
      } else {
        await http.post("/api/leads/bulk-delete", { ids });
      }
    } finally {
      setConfirmIds(null);
      setSelected(new Set());
      await refetch?.();
    }
  };

  // Archive flow ----------------------------------------------------------
  const requestArchive = (ids) => {
    const arr = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (arr.length) { setArchiveIds(arr); setArchiveReason(""); }
  };
  const cancelArchive = () => { setArchiveIds(null); setArchiveReason(""); };
  const doArchive = async () => {
    const ids = archiveIds || [];
    if (!ids.length) { setArchiveIds(null); return; }
    try {
      await http.post("/api/leads/bulk-archive", {
        ids, reason: archiveReason || null,
      });
    } finally {
      setArchiveIds(null);
      setArchiveReason("");
      setSelected(new Set());
      await refetch?.();
    }
  };

  return {
    selected, toggleOne, toggleAll, clear,
    confirmIds, requestDelete, doDelete, cancelDelete,
    archiveIds, archiveReason, setArchiveReason,
    requestArchive, doArchive, cancelArchive,
  };
}
